import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

const FORM_URL = "https://readdy.ai/api/form/db8gdkk6oi3g3tv8un1g";

export default function Footer() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value);
      }
    });
    setStatus("loading");
    setFormError("");
    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.includes("spam");
      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg && !isSpam
            ? serverMsg
            : "提交失败，请稍后重试",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试");
    }
  };

  return (
    <footer className="mt-16 bg-primary-950 text-primary-100">
      <div className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-8 md:py-16">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-500 text-background-50">
                <i className="ri-film-line text-lg"></i>
              </span>
              <span className="font-heading text-lg font-bold text-background-50">
                九七电影院
              </span>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-primary-200">
              九七电影院致力于为影迷提供最新上映电影信息、影院排片查询与影讯服务，让每一次观影都更简单。
            </p>
          </div>

          <div>
            <h4 className="font-heading text-sm font-semibold text-background-50">快速导航</h4>
            <ul className="mt-4 space-y-2 text-sm">
              {[
                { to: "/movies", label: "电影分类" },
                { to: "/cinemas", label: "影院排片" },
                { to: "/news", label: "影讯专题" },
                { to: "/about", label: "关于我们" },
              ].map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className="whitespace-nowrap text-primary-200 transition-colors hover:text-accent-400"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-sm font-semibold text-background-50">联系我们</h4>
            <ul className="mt-4 space-y-3 text-sm text-primary-200">
              <li className="flex items-center gap-2">
                <i className="ri-phone-line text-base text-accent-400"></i>
                <span>客服热线 400-977-9700</span>
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-mail-line text-base text-accent-400"></i>
                <span>service@jiuqi-cinema.com</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-map-pin-line text-base text-accent-400"></i>
                <span>北京市朝阳区三里屯路 19 号院九七大厦 8 层</span>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-sm font-semibold text-background-50">订阅影讯</h4>
            <p className="mt-4 text-sm text-primary-200">
              留下邮箱，第一时间获取最新排片与影讯。
            </p>
            <form
              id="newsletter-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-4"
            >
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="contact-verify-field"
              />
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="输入邮箱地址"
                  className="w-full rounded-md border border-primary-700 bg-primary-900 px-3 py-2 text-sm text-background-50 placeholder:text-primary-300 focus:border-accent-500 focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 disabled:opacity-60"
                >
                  {status === "loading" ? "提交中…" : "订阅"}
                </button>
              </div>
              {status === "success" && (
                <p className="mt-2 text-xs text-accent-400">订阅成功，感谢关注！</p>
              )}
              {status === "error" && (
                <p className="mt-2 text-xs text-accent-400">{formError}</p>
              )}
            </form>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-3 border-t border-primary-800 pt-6 text-xs text-primary-300 sm:flex-row sm:items-center sm:justify-between">
          <p>© 2026 九七电影院 Jiuqi Cinema. 保留所有权利。本网站为演示站点，购票信息仅供参考。</p>
          <p className="flex flex-wrap items-center gap-4">
            <span>合作伙伴：万达影城 · CGV 影城 · 保利国际影城</span>
          </p>
        </div>
      </div>
    </footer>
  );
}